import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import OtpInput from "react-otp-input";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import Cancle from "../../../assets/svg/cancle";
import {
  doSendMyEmailCode,
  doVerifyMyEmailCode,
} from "../../../actions/auth";
import { UPDATE_USER_DETAILS_S } from "../../../constants/types";
import {
  DEFAULT_RESEND_SECONDS,
  formatResendTimer,
} from "../../../utils/verification";
import "../../../pages/Otp Verify/verification.scss";
import "./verifyEmailModal.scss";

/**
 * Profile popup: send a 6-digit code to the user's email and verify it.
 * codeAlreadySent: the code went out already (e.g. right after an email change).
 */
function VerifyEmailModal({ isOpen, onClose, email, codeAlreadySent }) {
  const dispatch = useDispatch();
  const [code, setCode] = useState("");
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const sendCode = () => {
    setError("");
    setCode("");
    return dispatch(doSendMyEmailCode())
      .then((res) => {
        if (res?.data?.isEmailVerify) {
          dispatch({
            type: UPDATE_USER_DETAILS_S,
            payload: { data: { isEmailVerify: true } },
          });
          toast.success(res.message);
          onClose();
          return;
        }
        setTimer(res?.data?.cooldownSeconds ?? DEFAULT_RESEND_SECONDS);
      })
      .catch((err) => {
        setError(err?.message || "Could not send the code. Please try again.");
        if (err?.data?.cooldownSeconds) setTimer(err.data.cooldownSeconds);
      });
  };

  useEffect(() => {
    if (!isOpen) return;
    setCode("");
    setError("");
    if (codeAlreadySent) {
      setTimer(DEFAULT_RESEND_SECONDS);
    } else {
      sendCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || timer <= 0) return undefined;
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [isOpen, timer]);

  const verifyHandler = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    dispatch(doVerifyMyEmailCode({ code }))
      .then((res) => {
        dispatch({
          type: UPDATE_USER_DETAILS_S,
          payload: { data: { isEmailVerify: true } },
        });
        toast.success(res?.message || "Email verified successfully.");
        onClose();
      })
      .catch((err) => {
        setError(err?.message || "Verification failed. Please try again.");
        setCode("");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal
      className="custom-modal verify-email-modal"
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={onClose}
      contentLabel="Verify your email"
    >
      <div className="modal-header">
        <h3>Verify your email</h3>
        <button type="button" aria-label="Close" onClick={onClose}>
          <Cancle />
        </button>
      </div>
      <form className="modal-body form" onSubmit={verifyHandler}>
        <p className="verify-email-modal__text">
          Enter the 6-digit code we sent to <strong>{email}</strong>
        </p>
        <div className="input-list">
          <OtpInput
            isInputNum={true}
            value={code}
            onChange={setCode}
            numInputs={6}
            shouldAutoFocus={true}
            containerStyle={"otp-container"}
            className="otp-input"
            separator={<span></span>}
          />
        </div>
        {error && (
          <p
            className="verification_error verification_error--sentence"
            role="alert"
          >
            {error}
          </p>
        )}
        <div className="verify-email-modal__footer">
          {timer > 0 ? (
            <span className="verify-email-modal__timer">
              Resend code in {formatResendTimer(timer)}
            </span>
          ) : (
            <button
              type="button"
              className="verify-email-modal__resend"
              onClick={sendCode}
            >
              Resend code
            </button>
          )}
          <button
            type="submit"
            className="submit"
            disabled={code.length !== 6 || submitting}
          >
            Verify
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default VerifyEmailModal;
